import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from './supabase';
import './Hauler.css';

const BACKEND = 'https://tambo-production-826b.up.railway.app';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Resolves the Supabase token at call time so it works immediately after sign-in.
// Attaches Authorization: Bearer <token>. Refreshes and retries once on 401.
// Does NOT call signOut — callers handle session cleanup so there is no lock
// contention between a pending signOut and a subsequent getSession call.
async function clientFetch(url, options) {
  console.log('[CLIENT FETCH] called for', url);
  var sessionResult = await supabase.auth.getSession();
  var tok = sessionResult.data && sessionResult.data.session && sessionResult.data.session.access_token;
  if (!tok) {
    console.log('[CLIENT FETCH] no session');
    throw new Error('NOT_AUTHENTICATED');
  }
  console.log('[CLIENT FETCH] session found');
  function makeReq(t) {
    var merged = Object.assign({}, options, {
      headers: Object.assign({}, options && options.headers, { 'Authorization': 'Bearer ' + t }),
    });
    return fetch(url, merged);
  }
  var res = await makeReq(tok);
  console.log('[CLIENT FETCH] response status', res.status, 'for', url);
  if (res.status === 401) {
    console.log('[CLIENT FETCH] 401 — attempting token refresh');
    var refreshResult = await supabase.auth.refreshSession();
    var newTok = refreshResult.data && refreshResult.data.session && refreshResult.data.session.access_token;
    if (!newTok || refreshResult.error) {
      console.log('[CLIENT FETCH] refresh failed —', refreshResult.error ? refreshResult.error.message : 'no token');
      throw new Error('NOT_AUTHENTICATED');
    }
    res = await makeReq(newTok);
    console.log('[CLIENT FETCH] retry response status', res.status, 'for', url);
    if (res.status === 401) {
      throw new Error('NOT_AUTHENTICATED');
    }
  }
  return res;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconHome({ active }) {
  const c = active ? '#1A7A4A' : '#9CA3AF';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12L3 12L12 3L21 12L19 12"/>
      <path d="M5 12V19C5 19.5523 5.44772 20 6 20H9V16C9 15.4477 9.44772 15 10 15H14C14.5523 15 15 15.4477 15 16V20H18C18.5523 20 19 19.5523 19 19V12"/>
    </svg>
  );
}

function IconOrders({ active }) {
  const c = active ? '#1A7A4A' : '#9CA3AF';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="18" rx="2"/>
      <line x1="8" y1="8" x2="16" y2="8"/>
      <line x1="8" y1="12" x2="16" y2="12"/>
      <line x1="8" y1="16" x2="12" y2="16"/>
    </svg>
  );
}

function IconInvoice({ active }) {
  const c = active ? '#1A7A4A' : '#9CA3AF';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <path d="M9 9h6M9 12h6M9 15h4"/>
      <path d="M15 3v4h4"/>
    </svg>
  );
}

function IconUser({ active }) {
  const c = active ? '#1A7A4A' : '#9CA3AF';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="7" r="4"/>
      <path d="M4 21C4 17.134 7.58172 14 12 14C16.4183 14 20 17.134 20 21"/>
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(raw) {
  if (!raw) return '—';
  const parts = String(raw).split('T')[0].split('-').map(Number);
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  const today = new Date();
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtMoney(n) { return '$' + Number(n || 0).toFixed(2); }

function dispInvNum(inv) { return inv.invoice_ref || inv.invoice_display || ('INV-' + ((inv.invoice_number || 0) + 1000)); }

function invStatus(inv) {
  if (inv.status === 'paid') return 'paid';
  if (inv.status === 'sent' && inv.due_date && new Date(String(inv.due_date).split('T')[0] + 'T12:00:00') < new Date()) return 'overdue';
  return inv.status || 'draft';
}

const ORDER_STATUS = {
  pending:   { label: 'Pending',   color: '#D97706', bg: '#FEF3C7' },
  confirmed: { label: 'Confirmed', color: '#1D4ED8', bg: '#EFF6FF' },
  delivered: { label: 'Delivered', color: '#1A7A4A', bg: '#E8F5EE' },
  completed: { label: 'Completed', color: '#6B7280', bg: '#F3F4F6' },
  cancelled: { label: 'Cancelled', color: '#EF4444', bg: '#FEE2E2' },
};

const INV_STATUS = {
  paid:    { label: 'Paid',    color: '#10B981', bg: '#D1FAE5' },
  sent:    { label: 'Due',     color: '#1D4ED8', bg: '#EFF6FF' },
  overdue: { label: 'Overdue', color: '#EF4444', bg: '#FEE2E2' },
  draft:   { label: 'Draft',   color: '#6B7280', bg: '#F3F4F6' },
};

function StatusBadge({ status, map }) {
  const s = map[status] || map.draft || { label: status, color: '#6B7280', bg: '#F3F4F6' };
  return (
    <span style={{background:s.bg,color:s.color,padding:'3px 9px',borderRadius:'20px',fontSize:'10px',fontWeight:'700',whiteSpace:'nowrap'}}>
      {s.label}
    </span>
  );
}

// ─── Setup Card Form (Stripe Elements) ────────────────────────────────────────

function SetupCardInner({ userId, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async function() {
    if (!stripe || !elements) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(BACKEND + '/api/clients/' + userId + '/setup-card', { method: 'POST' });
      const data = await res.json();
      if (data.error) { setError(data.error); setLoading(false); return; }

      const result = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) { setError(result.error.message); setLoading(false); return; }

      await fetch(BACKEND + '/api/clients/' + userId + '/card-confirmed', { method: 'POST' });
      onSuccess();
    } catch (e) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div>
      <div style={{border:'1.5px solid #E0E0DA',borderRadius:'10px',padding:'14px',marginBottom:'12px',background:'white'}}>
        <CardElement options={{style:{base:{fontSize:'15px',color:'#0D0D0D','::placeholder':{color:'#aaa'}}}}}/>
      </div>
      {error && <div className="h-error">{error}</div>}
      <button className="h-btn" onClick={handleSave} disabled={loading || !stripe} style={{marginBottom:'8px'}}>
        {loading ? 'Saving...' : 'Save Card'}
      </button>
      <button onClick={onCancel} style={{width:'100%',padding:'12px',background:'none',border:'1.5px solid #E0E0DA',borderRadius:'10px',fontSize:'14px',fontWeight:'600',cursor:'pointer',color:'#6B6B6B'}}>
        Cancel
      </button>
    </div>
  );
}

function SetupCardForm({ userId, onSuccess, onCancel }) {
  return (
    <Elements stripe={stripePromise}>
      <SetupCardInner userId={userId} onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
}

// ─── Pending / Rejected screens ───────────────────────────────────────────────

function PendingPage({ onSignOut }) {
  return (
    <div style={{background:'#FAFAF8',minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'32px 24px',textAlign:'center',fontFamily:'sans-serif'}}>
      <div style={{fontSize:'26px',fontWeight:'800',color:'#0D0D0D',marginBottom:'32px'}}>Tam<span style={{color:'#1A7A4A'}}>bo</span></div>
      <div style={{width:'72px',height:'72px',background:'#FFF8E1',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'32px',marginBottom:'20px'}}>⏳</div>
      <div style={{fontSize:'20px',fontWeight:'800',color:'#0D0D0D',marginBottom:'10px'}}>Account Pending Approval</div>
      <div style={{fontSize:'14px',color:'#6B6B6B',maxWidth:'300px',lineHeight:'1.7',marginBottom:'8px'}}>
        Thanks for signing up! Your account is under review. You'll be notified once approved.
      </div>
      <div style={{fontSize:'14px',color:'#6B6B6B',maxWidth:'300px',lineHeight:'1.7',marginBottom:'36px'}}>
        Questions? Call <a href="tel:+19562263903" style={{color:'#1A7A4A',fontWeight:'600',textDecoration:'none'}}>(956) 226-3903</a>
      </div>
      <button onClick={onSignOut} style={{background:'#6B6B6B',color:'white',border:'none',borderRadius:'10px',padding:'12px 28px',fontSize:'14px',fontWeight:'700',cursor:'pointer'}}>
        Log Out
      </button>
    </div>
  );
}

// ─── Dashboard Tabs ───────────────────────────────────────────────────────────

function HomeTab({ profile, orders, onBookNow, onViewOrder }) {
  const active = orders.filter(function(o) { return o.status === 'confirmed' || o.status === 'delivered'; });
  const upcoming = orders.find(function(o) { return o.status === 'confirmed'; });
  const totalSpent = orders.reduce(function(s, o) { return s + parseFloat(o.total_charged || 0); }, 0);

  return (
    <div>
      {/* Stats */}
      <div className="h-stat-row">
        <div className="h-stat-card">
          <div className="h-stat-val">{active.length}</div>
          <div className="h-stat-lbl">Active Orders</div>
        </div>
        <div className="h-stat-card">
          <div className="h-stat-val">{orders.length}</div>
          <div className="h-stat-lbl">Total Orders</div>
        </div>
        <div className="h-stat-card green">
          <div className="h-stat-val">{fmtMoney(totalSpent)}</div>
          <div className="h-stat-lbl">Total Spent</div>
        </div>
      </div>

      {/* Book button */}
      <div style={{padding:'4px 14px 14px'}}>
        <button
          onClick={onBookNow}
          style={{width:'100%',padding:'15px',background:'#1A7A4A',color:'white',border:'none',borderRadius:'12px',fontSize:'15px',fontWeight:'700',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'8px'}}
        >
          🚛 Book a Dumpster
        </button>
      </div>

      {/* Next delivery */}
      {upcoming && (
        <div className="h-section">
          <div className="h-section-hd">Next Delivery</div>
          <div className="h-upcoming-card" onClick={function() { onViewOrder(upcoming); }} style={{cursor:'pointer'}}>
            <div className="h-upcoming-label">DELIVER ON</div>
            <div className="h-upcoming-date">{fmtDate(upcoming.requested_delivery_date)}</div>
            <div className="h-upcoming-addr">{upcoming.delivery_address}</div>
            <div className="h-upcoming-meta">{upcoming.size} · {fmtMoney(upcoming.total_charged)}</div>
          </div>
        </div>
      )}

      {/* Recent orders */}
      {orders.length > 0 && (
        <div className="h-section">
          <div className="h-section-hd">Recent Orders</div>
          <div className="h-list-card">
            {orders.slice(0, 4).map(function(order) {
              return (
                <div className="h-job-row" key={order.id} onClick={function() { onViewOrder(order); }}>
                  <div className="h-job-date">{fmtDate(order.requested_delivery_date)}</div>
                  <div className="h-job-info">
                    <div className="h-job-addr">{order.delivery_address || '—'}</div>
                    <div className="h-job-meta">{order.size} · {fmtMoney(order.total_charged)}</div>
                  </div>
                  <StatusBadge status={order.status} map={ORDER_STATUS} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {orders.length === 0 && (
        <div className="h-section">
          <div className="h-empty">
            <div className="h-empty-icon">🚛</div>
            <div className="h-empty-title">No orders yet</div>
            <div>Book your first dumpster delivery above.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function RequestModal({ type, order, user, onClose }) {
  const [reasonType, setReasonType] = useState('Weather');
  const [reason, setReason] = useState('');
  const [proposedDate, setProposedDate] = useState('');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const CANCEL_REASONS = ['Weather', 'Project Delay', 'No Longer Needed', 'Other'];

  const handleSubmit = async function() {
    setSubmitting(true);
    setError('');
    try {
      const endpoint = type === 'cancel' ? '/api/requests/cancel' : '/api/requests/reschedule';
      const body = {
        order_id: order.id,
        reason: type === 'cancel' ? (reasonType === 'Other' ? reason : reasonType) : (reason || null),
      };
      if (type === 'reschedule') {
        if (!proposedDate) { setError('Please select a proposed date.'); setSubmitting(false); return; }
        body.proposed_date = proposedDate;
        body.proposed_window_start = windowStart || null;
        body.proposed_window_end = windowEnd || null;
      }
      const res = await clientFetch(BACKEND + endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const overlayStyle = { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center' };
  const sheetStyle = { background:'#fff', borderRadius:'20px 20px 0 0', padding:'20px 20px 40px', width:'100%', maxWidth:'480px', boxSizing:'border-box' };
  const inputStyle = { width:'100%', boxSizing:'border-box', border:'1.5px solid #E0E0DA', borderRadius:'10px', padding:'10px 12px', fontSize:'14px', outline:'none', background:'white' };
  const labelStyle = { fontSize:'11px', fontWeight:'700', color:'#6B6B6B', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'5px', display:'block' };

  if (done) {
    const msg = type === 'cancel'
      ? 'Your cancellation request has been sent. Our team will review and contact you shortly.'
      : 'Your reschedule request has been sent. Our team will confirm the new date with you.';
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={sheetStyle} onClick={function(e){e.stopPropagation();}}>
          <div style={{textAlign:'center',padding:'16px 0'}}>
            <div style={{fontSize:'32px',marginBottom:'10px'}}>✅</div>
            <div style={{fontSize:'16px',fontWeight:'800',color:'#0D0D0D',marginBottom:'8px'}}>Request Sent!</div>
            <div style={{fontSize:'13px',color:'#6B6B6B',lineHeight:'1.6',marginBottom:'24px'}}>{msg}</div>
            <button onClick={onClose} style={{background:'#1A7A4A',color:'white',border:'none',borderRadius:'10px',padding:'12px 28px',fontSize:'14px',fontWeight:'700',cursor:'pointer'}}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={function(e){e.stopPropagation();}}>
        <div style={{width:'36px',height:'4px',background:'#E0E0DA',borderRadius:'2px',margin:'0 auto 18px'}} />
        <div style={{fontSize:'17px',fontWeight:'800',color:'#0D0D0D',marginBottom:'3px'}}>
          {type === 'cancel' ? 'Request Cancellation' : 'Request Reschedule'}
        </div>
        <div style={{fontSize:'12px',color:'#9CA3AF',marginBottom:'20px'}}>Order #{order.order_number}</div>

        {type === 'cancel' && (
          <>
            <label style={labelStyle}>Reason</label>
            <select value={reasonType} onChange={function(e){setReasonType(e.target.value);}} style={{...inputStyle,marginBottom:'12px'}}>
              {CANCEL_REASONS.map(function(r) { return <option key={r} value={r}>{r}</option>; })}
            </select>
            {reasonType === 'Other' && (
              <>
                <label style={labelStyle}>Please describe</label>
                <textarea value={reason} onChange={function(e){setReason(e.target.value);}} rows={3}
                  placeholder="Tell us more..." style={{...inputStyle,resize:'vertical',marginBottom:'12px'}} />
              </>
            )}
          </>
        )}

        {type === 'reschedule' && (
          <>
            <label style={labelStyle}>Proposed New Date <span style={{color:'#EF4444'}}>*</span></label>
            <input type="date" value={proposedDate} onChange={function(e){setProposedDate(e.target.value);}}
              min={new Date().toISOString().slice(0,10)} style={{...inputStyle,marginBottom:'12px'}} />
            <label style={labelStyle}>Preferred Time Window (optional)</label>
            <div style={{display:'flex',gap:'8px',marginBottom:'12px'}}>
              <input type="time" value={windowStart} onChange={function(e){setWindowStart(e.target.value);}} style={{...inputStyle,flex:1}} />
              <input type="time" value={windowEnd} onChange={function(e){setWindowEnd(e.target.value);}} style={{...inputStyle,flex:1}} />
            </div>
            <label style={labelStyle}>Reason (optional)</label>
            <input type="text" value={reason} onChange={function(e){setReason(e.target.value);}}
              placeholder="Why do you need to reschedule?" style={{...inputStyle,marginBottom:'12px'}} />
          </>
        )}

        {error && <div style={{color:'#EF4444',fontSize:'12px',marginBottom:'10px'}}>{error}</div>}

        <div style={{display:'flex',gap:'10px',marginTop:'6px'}}>
          <button onClick={onClose} disabled={submitting}
            style={{flex:1,padding:'12px',border:'1.5px solid #E0E0DA',borderRadius:'10px',background:'white',fontSize:'14px',fontWeight:'600',cursor:'pointer',color:'#374151'}}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            style={{flex:1,padding:'12px',border:'none',borderRadius:'10px',background:'#1A7A4A',color:'white',fontSize:'14px',fontWeight:'700',cursor:'pointer',opacity:submitting?0.7:1}}>
            {submitting ? 'Sending...' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrdersTab({ orders, onViewOrder, user }) {
  const [modal, setModal] = useState(null); // { type: 'cancel'|'reschedule', order }
  const [pastVisible, setPastVisible] = useState(10);
  const pastListRef = useRef(null);

  const active = orders.filter(function(o) { return o.status !== 'completed' && o.status !== 'cancelled'; });
  const past = orders.filter(function(o) { return o.status === 'completed' || o.status === 'cancelled'; });

  const pendingBadge = { fontSize:'10px', fontWeight:'700', background:'#FEF3C7', color:'#D97706', padding:'2px 8px', borderRadius:'10px', whiteSpace:'nowrap' };

  return (
    <div>
      <div className="h-section" style={{paddingTop:'16px'}}>
        <div className="h-section-hd" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span>Active Orders</span>
          {active.length > 0 && <span className="h-count-pill">{active.length}</span>}
        </div>
        {active.length === 0 ? (
          <div className="h-empty">
            <div className="h-empty-icon">📋</div>
            <div className="h-empty-title">No active orders</div>
            <div>Your active deliveries will appear here.</div>
          </div>
        ) : (
          <div className="h-list-card">
            {active.map(function(order) {
              const hasPending = order.cancel_requested || order.reschedule_requested;
              return (
                <div key={order.id}>
                  <div className="h-job-row" style={{borderBottom:'none'}} onClick={function() { onViewOrder(order); }}>
                    <div style={{flexShrink:0,minWidth:42}}>
                      <div style={{fontSize:'11px',fontWeight:'700',color:'#6B6B6B'}}>{fmtDate(order.requested_delivery_date)}</div>
                      <div style={{fontSize:'10px',color:'#9CA3AF',marginTop:'2px'}}>#{order.order_number}</div>
                    </div>
                    <div className="h-job-info">
                      <div className="h-job-addr">{order.delivery_address || '—'}</div>
                      <div className="h-job-meta">{order.size} · {fmtMoney(order.total_charged)}</div>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:'4px',alignItems:'flex-end'}}>
                      <StatusBadge status={order.status} map={ORDER_STATUS} />
                      <a href={'/order/' + order.id} onClick={function(e){e.stopPropagation();}}
                         style={{fontSize:'10px',color:'#1A7A4A',fontWeight:'700',textDecoration:'none'}}>Track →</a>
                    </div>
                  </div>
                  <div style={{padding:'4px 14px 12px',borderBottom:'1px solid #F0F0EE',display:'flex',gap:'6px',flexWrap:'wrap',alignItems:'center'}}>
                    {hasPending ? (
                      <>
                        {order.cancel_requested && <span style={pendingBadge}>Cancel Request Pending</span>}
                        {order.reschedule_requested && <span style={pendingBadge}>Reschedule Req. Pending</span>}
                      </>
                    ) : (
                      <>
                        <button onClick={function(){ setModal({ type: 'reschedule', order: order }); }}
                          style={{border:'1px solid #1A7A4A',color:'#1A7A4A',background:'transparent',padding:'4px 10px',borderRadius:'6px',fontSize:'11px',fontWeight:'600',cursor:'pointer'}}>
                          Reschedule
                        </button>
                        <button onClick={function(){ setModal({ type: 'cancel', order: order }); }}
                          style={{border:'1px solid #EF4444',color:'#EF4444',background:'transparent',padding:'4px 10px',borderRadius:'6px',fontSize:'11px',fontWeight:'600',cursor:'pointer'}}>
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {past.length > 0 && (
        <div className="h-section" ref={pastListRef} style={{scrollMarginTop:'70px'}}>
          <div className="h-section-hd">Order History</div>
          <div className="h-list-card">
            {past.slice(0, pastVisible).map(function(order) {
              return (
                <div className="h-job-row" key={order.id} onClick={function() { onViewOrder(order); }}>
                  <div style={{flexShrink:0,minWidth:42}}>
                    <div style={{fontSize:'11px',fontWeight:'700',color:'#9CA3AF'}}>{fmtDate(order.requested_delivery_date)}</div>
                    <div style={{fontSize:'10px',color:'#C4C4C0',marginTop:'2px'}}>#{order.order_number}</div>
                  </div>
                  <div className="h-job-info">
                    <div className="h-job-addr">{order.delivery_address || '—'}</div>
                    <div className="h-job-meta">{order.size} · {fmtMoney(order.total_charged)} · {order.hauler_name || 'Local Hauler'}</div>
                  </div>
                  <StatusBadge status={order.status} map={ORDER_STATUS} />
                </div>
              );
            })}
          </div>
          <div style={{textAlign:'center',padding:'12px 14px 4px'}}>
            <div style={{fontSize:'12px',color:'#9CA3AF',marginBottom:'8px'}}>
              Showing {Math.min(pastVisible, past.length)} of {past.length}
            </div>
            <div style={{display:'flex',gap:'8px',justifyContent:'center',flexWrap:'wrap'}}>
              {past.length > pastVisible && (
                <button
                  onClick={function() { setPastVisible(function(v) { return v + 10; }); }}
                  style={{background:'#1A7A4A',color:'white',border:'none',borderRadius:'8px',padding:'9px 24px',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}
                >
                  Show More
                </button>
              )}
              {pastVisible > 10 && (
                <button
                  onClick={function() { setPastVisible(10); pastListRef.current?.scrollIntoView({behavior:'smooth',block:'start'}); }}
                  style={{background:'transparent',color:'#1A7A4A',border:'2px solid #1A7A4A',borderRadius:'8px',padding:'7px 24px',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}
                >
                  Show Less
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {modal && (
        <RequestModal
          type={modal.type}
          order={modal.order}
          user={user}
          onClose={function(){ setModal(null); }}
        />
      )}
    </div>
  );
}

function InvoicesTab({ invoices }) {
  const [payingId,    setPayingId]    = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [multiPaying, setMultiPaying] = useState(false);
  const [payError,    setPayError]    = useState('');
  const [invVisible,  setInvVisible]  = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const invListRef = useRef(null);

  const totalSpent = invoices.reduce(function(s, i) {
    return i.status === 'paid' ? s + parseFloat(i.total || 0) : s;
  }, 0);
  const pendingDue = invoices.reduce(function(s, i) {
    return i.status !== 'paid' ? s + parseFloat(i.total || 0) : s;
  }, 0);
  const selectedTotal = invoices
    .filter(function(i) { return selectedIds.has(i.id); })
    .reduce(function(s, i) { return s + parseFloat(i.total || 0); }, 0);

  const invSorted = invoices.slice().sort(function(a, b) { return new Date(b.created_at) - new Date(a.created_at); });
  const invFiltered = statusFilter === 'paid'
    ? invSorted.filter(function(i) { return invStatus(i) === 'paid'; })
    : statusFilter === 'pending'
    ? invSorted.filter(function(i) { return invStatus(i) !== 'paid'; })
    : invSorted;

  function toggleSelect(id) {
    setSelectedIds(function(prev) {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function payOne(inv) {
    setPayingId(inv.id);
    setPayError('');
    try {
      let url = inv.stripe_payment_link;
      if (!url) {
        const res = await fetch(BACKEND + '/api/invoices/' + inv.id + '/pay-link', { method: 'POST' });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to create payment link');
        url = data.url;
      }
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setPayError(err.message);
    } finally {
      setPayingId(null);
    }
  }

  async function paySelected() {
    if (selectedIds.size === 0) return;
    setMultiPaying(true);
    setPayError('');
    try {
      const ids = Array.from(selectedIds);
      if (ids.length === 1) {
        const inv = invoices.find(function(i) { return i.id === ids[0]; });
        if (inv && inv.stripe_payment_link) {
          window.open(inv.stripe_payment_link, '_blank', 'noopener');
          return;
        }
      }
      const res = await fetch(BACKEND + '/api/invoices/batch-pay-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_ids: ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create payment session');
      window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      setPayError(err.message);
    } finally {
      setMultiPaying(false);
    }
  }

  return (
    <div>
      {/* Summary cards */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',padding:'16px 14px 4px'}}>
        <div style={{background:'white',borderRadius:'12px',padding:'14px',border:'1px solid #E8E8E4'}}>
          <div style={{fontSize:'10px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:'6px'}}>Total Spent</div>
          <div style={{fontSize:'20px',fontWeight:'800',color:'#0D0D0D'}}>{fmtMoney(totalSpent)}</div>
        </div>
        <div style={{background:'white',borderRadius:'12px',padding:'14px',border:'1px solid #E8E8E4'}}>
          <div style={{fontSize:'10px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:'6px'}}>Pending Due</div>
          <div style={{fontSize:'20px',fontWeight:'800',color:pendingDue > 0 ? '#EF4444' : '#0D0D0D'}}>{fmtMoney(pendingDue)}</div>
        </div>
      </div>

      {/* Multi-select payment bar */}
      {selectedIds.size > 0 && (
        <div style={{margin:'10px 14px 0',background:'#1A7A4A',borderRadius:'12px',padding:'14px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:'10px'}}>
          <div>
            <div style={{color:'white',fontWeight:'800',fontSize:'14px'}}>{selectedIds.size} invoice{selectedIds.size > 1 ? 's' : ''} selected</div>
            <div style={{color:'rgba(255,255,255,0.8)',fontSize:'12px',marginTop:'2px'}}>{fmtMoney(selectedTotal)} total</div>
          </div>
          <button onClick={paySelected} disabled={multiPaying}
            style={{background:'white',color:'#1A7A4A',border:'none',borderRadius:'8px',padding:'9px 18px',fontSize:'13px',fontWeight:'800',cursor:'pointer',opacity:multiPaying?0.7:1,whiteSpace:'nowrap',flexShrink:0}}>
            {multiPaying ? 'Opening...' : 'Pay Now'}
          </button>
        </div>
      )}

      {payError && (
        <div style={{margin:'10px 14px 0',background:'#FEE2E2',color:'#DC2626',borderRadius:'8px',padding:'10px 12px',fontSize:'12px',fontWeight:'600'}}>{payError}</div>
      )}

      {/* Status filter toggle */}
      {invoices.length > 0 && (
        <div style={{padding:'12px 14px 0',display:'flex',gap:'0'}}>
          <div style={{display:'flex',border:'1px solid #E8E8E4',borderRadius:'8px',overflow:'hidden'}}>
            {[{val:'all',label:'All'},{val:'pending',label:'Pending'},{val:'paid',label:'Paid'}].map(function(opt,i){
              var active = statusFilter === opt.val;
              return (
                <button key={opt.val} onClick={function(){ setStatusFilter(opt.val); setInvVisible(10); }}
                  style={{padding:'7px 16px',background:active?'#1A7A4A':'white',color:active?'white':'#374151',border:'none',borderLeft:i>0?'1px solid #E8E8E4':'none',fontSize:'13px',fontWeight:active?700:500,cursor:'pointer',whiteSpace:'nowrap'}}>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="h-section" ref={invListRef} style={{paddingTop:'12px',scrollMarginTop:'70px'}}>
        {invFiltered.length === 0 ? (
          <div className="h-empty">
            <div className="h-empty-icon">📄</div>
            <div className="h-empty-title">{invoices.length === 0 ? 'No invoices yet' : 'No ' + (statusFilter === 'paid' ? 'paid' : statusFilter === 'pending' ? 'pending' : '') + ' invoices'}</div>
            <div>{invoices.length === 0 ? 'Invoices from your orders will appear here.' : 'Try a different filter.'}</div>
          </div>
        ) : (
          <div className="h-list-card">
            {invFiltered.slice(0, invVisible).map(function(inv) {
              const st       = invStatus(inv);
              const isPaid   = st === 'paid';
              const isDraft  = st === 'draft';
              const canPay   = !isPaid && !isDraft;
              const isSelected = selectedIds.has(inv.id);
              const isLoading  = payingId === inv.id;
              const desc = inv.line_items && inv.line_items[0] ? inv.line_items[0].description : '';

              return (
                <div key={inv.id} style={{borderBottom:'1px solid #F0F0EE',padding:'14px',background:isSelected?'#F0FDF4':'white',transition:'background 0.15s'}}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'4px',gap:'8px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:'8px',flex:1,minWidth:0}}>
                      {canPay && (
                        <input type="checkbox" checked={isSelected}
                          onChange={function(){ toggleSelect(inv.id); }}
                          style={{width:'16px',height:'16px',accentColor:'#1A7A4A',flexShrink:0,cursor:'pointer',marginTop:'1px'}} />
                      )}
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:'14px',fontWeight:'700',color:'#0D0D0D'}}>{dispInvNum(inv)}</div>
                        <div style={{fontSize:'11px',color:'#9CA3AF',marginTop:'2px'}}>{fmtDate(inv.created_at)}</div>
                      </div>
                    </div>
                    <StatusBadge status={st} map={INV_STATUS} />
                  </div>
                  {desc ? <div style={{fontSize:'12px',color:'#6B6B6B',marginBottom:'8px',marginTop:'4px',paddingLeft:canPay?'24px':'0'}}>{desc}</div> : null}
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'6px',paddingLeft:canPay?'24px':'0'}}>
                    <div style={{fontSize:'18px',fontWeight:'800',color:isPaid?'#0D0D0D':'#EF4444'}}>{fmtMoney(inv.total)}</div>
                    <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                      {canPay && (
                        <button onClick={function(){ payOne(inv); }} disabled={isLoading || !!payingId}
                          style={{background:'#1A7A4A',color:'white',padding:'6px 14px',borderRadius:'7px',fontSize:'12px',fontWeight:'700',border:'none',cursor:'pointer',opacity:(isLoading||!!payingId)?0.7:1,whiteSpace:'nowrap'}}>
                          {isLoading ? 'Opening...' : 'Pay Now'}
                        </button>
                      )}
                      <a href={'/invoice/' + inv.id}
                        style={{fontSize:'12px',color:'#6B6B6B',fontWeight:'600',textDecoration:'none'}}>
                        View
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {invFiltered.length > 0 && (
          <div style={{textAlign:'center',padding:'12px 14px 4px'}}>
            <div style={{fontSize:'12px',color:'#9CA3AF',marginBottom:'8px'}}>
              Showing {Math.min(invVisible, invFiltered.length)} of {invFiltered.length}
            </div>
            <div style={{display:'flex',gap:'8px',justifyContent:'center',flexWrap:'wrap',marginBottom:'8px'}}>
              {invFiltered.length > invVisible && (
                <button
                  onClick={function() { setInvVisible(function(v) { return v + 10; }); }}
                  style={{background:'#1A7A4A',color:'white',border:'none',borderRadius:'8px',padding:'9px 24px',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}
                >
                  Show More
                </button>
              )}
              {invVisible > 10 && (
                <button
                  onClick={function() { setInvVisible(10); invListRef.current?.scrollIntoView({behavior:'smooth',block:'start'}); }}
                  style={{background:'transparent',color:'#1A7A4A',border:'2px solid #1A7A4A',borderRadius:'8px',padding:'7px 24px',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}
                >
                  Show Less
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileTab({ user, profile, onProfileSaved, onCardAdded }) {
  const [form, setForm] = useState({
    first_name:      profile.first_name      || '',
    last_name:       profile.last_name       || '',
    phone:           profile.phone           || '',
    company_name:    profile.company_name    || '',
    billing_address: profile.billing_address || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showCardForm, setShowCardForm] = useState(false);
  const billingRef = useRef(null);
  const billingAcRef = useRef(null);

  // Google Places for billing address
  useEffect(function() {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) return;
    function initAc() {
      if (!window.google?.maps?.places || !billingRef.current || billingAcRef.current) return;
      billingAcRef.current = new window.google.maps.places.Autocomplete(
        billingRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' }, fields: ['formatted_address'] }
      );
      billingAcRef.current.addListener('place_changed', function() {
        const place = billingAcRef.current.getPlace();
        if (place?.formatted_address) setForm(function(f) { return Object.assign({}, f, { billing_address: place.formatted_address }); });
      });
    }
    if (window.google?.maps?.places) { setTimeout(initAc, 50); return; }
    if (!document.getElementById('gmaps-script')) {
      window.__gmapsReady = initAc;
      const s = document.createElement('script');
      s.id = 'gmaps-script';
      s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&solution_channel=GMP_QB_addressselection_v1&callback=__gmapsReady`;
      document.head.appendChild(s);
    } else {
      const timer = setInterval(function() { if (window.google?.maps?.places) { clearInterval(timer); initAc(); } }, 200);
      return function() { clearInterval(timer); };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async function() {
    setSaving(true); setSaveError(''); setSaved(false);
    try {
      const res = await clientFetch(BACKEND + '/api/clients/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({ user_id: user.id }, form)),
      });
      const data = await res.json();
      if (data.error) { setSaveError(data.error); } else { setSaved(true); onProfileSaved(data.profile); }
    } catch(e) { setSaveError('Network error. Please try again.'); }
    setSaving(false);
  };

  const field = function(label, key, opts) {
    return (
      <div className="h-form-field" key={key}>
        <label className="h-form-label">{label}</label>
        <input
          ref={key === 'billing_address' ? billingRef : undefined}
          className="h-form-input"
          type={opts?.type || 'text'}
          placeholder={opts?.placeholder || ''}
          value={form[key]}
          onChange={function(e) { setForm(function(f) { return Object.assign({}, f, { [key]: e.target.value }); }); }}
        />
      </div>
    );
  };

  const payLaterColor = profile.pay_later_approved ? '#1A7A4A' : '#D97706';
  const payLaterBg = profile.pay_later_approved ? '#E8F5EE' : '#FEF3C7';
  const payLaterLabel = profile.pay_later_approved ? 'Approved' : 'Not approved';

  return (
    <div className="h-section" style={{paddingTop:'16px'}}>

      {/* Personal info */}
      <div className="h-section-hd">Personal Info</div>
      <div className="h-card" style={{marginBottom:'14px'}}>
        <div style={{display:'flex',gap:'10px'}}>
          <div style={{flex:1}}>{field('First Name', 'first_name', { placeholder: 'John' })}</div>
          <div style={{flex:1}}>{field('Last Name', 'last_name', { placeholder: 'Smith' })}</div>
        </div>
        {field('Cell Phone', 'phone', { type: 'tel', placeholder: '(555) 555-5555' })}
        <div className="h-form-field">
          <label className="h-form-label">Email</label>
          <input className="h-form-input" value={user.email} disabled />
        </div>
      </div>

      {/* Business */}
      <div className="h-section-hd">Business</div>
      <div className="h-card" style={{marginBottom:'14px'}}>
        {field('Company Name', 'company_name', { placeholder: 'Acme Corp (optional)' })}
        {field('Billing Address', 'billing_address', { placeholder: '123 Main St, City, TX' })}
      </div>

      {saved && <div className="h-success">Profile updated!</div>}
      {saveError && <div className="h-error">{saveError}</div>}
      <button className="h-btn" onClick={handleSave} disabled={saving} style={{marginBottom:'20px'}}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>

      {/* Card on file */}
      <div className="h-section-hd">Payment</div>
      <div className="h-card" style={{marginBottom:'14px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom: showCardForm ? '16px' : '0'}}>
          <div>
            <div style={{fontSize:'14px',fontWeight:'700',color:'#0D0D0D'}}>Card on File</div>
            <div style={{fontSize:'12px',color: profile.has_card_on_file ? '#1A7A4A' : '#9CA3AF',marginTop:'2px'}}>
              {profile.has_card_on_file ? '✓ Card saved' : 'No card saved'}
            </div>
          </div>
          {!showCardForm && (
            <button
              onClick={function() { setShowCardForm(true); }}
              style={{background:profile.has_card_on_file ? '#F3F4F6' : '#1A7A4A',color:profile.has_card_on_file ? '#374151' : 'white',border:'none',borderRadius:'8px',padding:'7px 14px',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}
            >
              {profile.has_card_on_file ? 'Update Card' : 'Add Card'}
            </button>
          )}
        </div>
        {showCardForm && (
          <SetupCardForm
            userId={user.id}
            onSuccess={function() { setShowCardForm(false); onCardAdded(); }}
            onCancel={function() { setShowCardForm(false); }}
          />
        )}
      </div>

      {/* Pay Later */}
      <div className="h-section-hd">Pay Later</div>
      <div className="h-card" style={{marginBottom:'24px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div style={{fontSize:'14px',fontWeight:'700',color:'#0D0D0D'}}>Invoice Terms</div>
            <div style={{fontSize:'12px',color:'#6B6B6B',marginTop:'2px'}}>Pay via invoice after delivery</div>
          </div>
          <span style={{background:payLaterBg,color:payLaterColor,padding:'4px 12px',borderRadius:'20px',fontSize:'11px',fontWeight:'700'}}>
            {payLaterLabel}
          </span>
        </div>
        {!profile.pay_later_approved && (
          <div style={{marginTop:'12px',fontSize:'12px',color:'#6B6B6B',lineHeight:'1.6'}}>
            Contact us to request invoice terms. Call <a href="tel:+19562263903" style={{color:'#1A7A4A',fontWeight:'600',textDecoration:'none'}}>(956) 226-3903</a>
          </div>
        )}
      </div>

    </div>
  );
}

// ─── Order Detail Sheet ───────────────────────────────────────────────────────

function OrderDetailSheet({ order, onClose }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:300,display:'flex',alignItems:'flex-end'}} onClick={onClose}>
      <div
        style={{background:'white',borderRadius:'20px 20px 0 0',width:'100%',maxWidth:'480px',margin:'0 auto',padding:'24px 20px 36px',maxHeight:'80vh',overflowY:'auto'}}
        onClick={function(e){e.stopPropagation();}}
      >
        <div style={{width:'40px',height:'4px',background:'#E0E0DA',borderRadius:'2px',margin:'0 auto 20px'}}/>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'16px'}}>
          <div>
            <div style={{fontSize:'16px',fontWeight:'800',color:'#0D0D0D'}}>Order #{order.order_number}</div>
            <div style={{marginTop:'4px'}}><StatusBadge status={order.status} map={ORDER_STATUS} /></div>
          </div>
          <a href={'/order/' + order.id} style={{background:'#1A7A4A',color:'white',padding:'7px 14px',borderRadius:'8px',fontSize:'12px',fontWeight:'700',textDecoration:'none'}}>
            Track Order
          </a>
        </div>
        {[
          ['Delivery Address', order.delivery_address],
          ['Dumpster Size', order.size],
          ['Delivery Date', fmtDate(order.requested_delivery_date)],
          ['Hauler', order.hauler_name || 'Assigned soon'],
          ['Total', fmtMoney(order.total_charged)],
        ].map(function([label, val]) {
          return val ? (
            <div key={label} style={{display:'flex',justifyContent:'space-between',padding:'10px 0',borderBottom:'1px solid #F0F0EE',fontSize:'13px'}}>
              <span style={{color:'#6B6B6B'}}>{label}</span>
              <span style={{fontWeight:'600',color:'#0D0D0D',textAlign:'right',maxWidth:'60%'}}>{val}</span>
            </div>
          ) : null;
        })}
      </div>
    </div>
  );
}

// ─── Main Dashboard Shell ─────────────────────────────────────────────────────

function ClientDashboard({ user, profile: initProfile, orders, invoices, onSignOut, onRefresh }) {
  const [tab, setTab] = useState('home');
  const [profile, setProfile] = useState(initProfile);
  const [hasCard, setHasCard] = useState(initProfile.has_card_on_file);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const displayName = profile.first_name || user.email.split('@')[0];

  const unpaidInvoices = invoices.filter(function(inv) {
    return inv.status !== 'paid' && inv.status !== 'draft';
  });
  const unpaidTotal = unpaidInvoices.reduce(function(sum, inv) {
    return sum + parseFloat(inv.total || 0);
  }, 0);

  return (
    <div className="h-app">
      <header className="h-header">
        <div className="h-header-logo" onClick={function(){setTab('home');}} style={{cursor:'pointer'}}>Tam<span>bo</span></div>
        <div className="h-header-name">Hi, {displayName}</div>
        <button className="h-header-logout" onClick={onSignOut}>Logout</button>
      </header>

      {unpaidInvoices.length > 0 && (
        <div style={{background:'#FEF3C7',borderBottom:'1px solid #FDE68A',padding:'10px 16px',display:'flex',alignItems:'center',gap:'10px',flexShrink:0}}>
          <span style={{fontSize:'16px',flexShrink:0}}>⚠️</span>
          <span style={{flex:1,fontSize:'13px',fontWeight:'600',color:'#78350F',lineHeight:'1.4'}}>
            {unpaidInvoices.length === 1
              ? `You have 1 unpaid invoice totaling ${fmtMoney(unpaidTotal)}`
              : `You have ${unpaidInvoices.length} unpaid invoices totaling ${fmtMoney(unpaidTotal)}`}
          </span>
          <button
            onClick={function(){ setTab('invoices'); }}
            style={{background:'#D97706',color:'white',border:'none',borderRadius:'8px',padding:'6px 14px',fontSize:'12px',fontWeight:'700',cursor:'pointer',flexShrink:0,whiteSpace:'nowrap'}}
          >
            Pay Now
          </button>
        </div>
      )}

      <main className="h-main">
        {tab === 'home' && (
          <HomeTab
            profile={profile}
            orders={orders}
            onBookNow={function(){window.location.href='/';}}
            onViewOrder={function(o){setSelectedOrder(o);}}
          />
        )}
        {tab === 'orders' && (
          <OrdersTab orders={orders} onViewOrder={function(o){setSelectedOrder(o);}} user={user} />
        )}
        {tab === 'invoices' && (
          <InvoicesTab invoices={invoices} />
        )}
        {tab === 'profile' && (
          <ProfileTab
            user={user}
            profile={Object.assign({}, profile, { has_card_on_file: hasCard })}
            onProfileSaved={function(p) { setProfile(p); }}
            onCardAdded={function() { setHasCard(true); onRefresh(); }}
          />
        )}
      </main>

      {/* Bottom Nav */}
      <nav className="h-bottom-nav">
        {[
          { key: 'home',     label: 'Home',     Icon: IconHome },
          { key: 'orders',   label: 'Orders',   Icon: IconOrders },
          { key: 'invoices', label: 'Invoices', Icon: IconInvoice },
          { key: 'profile',  label: 'Profile',  Icon: IconUser },
        ].map(function(t) {
          const active = tab === t.key;
          return (
            <button key={t.key} className={'h-tab-btn' + (active ? ' active' : '')} onClick={function(){setTab(t.key);}}>
              <t.Icon active={active} />
              <span className="h-tab-label">{t.label}</span>
            </button>
          );
        })}
      </nav>

      {selectedOrder && (
        <OrderDetailSheet order={selectedOrder} onClose={function(){setSelectedOrder(null);}} />
      )}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

function Client() {
  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [noSession, setNoSession] = useState(false);

  async function loadData(user) {
    setLoadError('');
    try {
      const res = await clientFetch(BACKEND + '/api/clients/me');
      if (!res.ok) {
        const errData = await res.json().catch(function() { return {}; });
        throw new Error(errData.message || errData.error || 'Server error (' + res.status + ')');
      }
      const data = await res.json();
      setProfile(data.profile);
      setOrders(data.orders || []);
      setInvoices(data.invoices || []);
    } catch (e) {
      if (e.message === 'NOT_AUTHENTICATED') {
        await supabase.auth.signOut(); // await so the lock is released before any navigation
        window.location.href = '/login';
        return; // finally still runs to clear loading
      }
      console.error('[loadData] error:', e.message);
      setLoadError('Could not load your account. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;

        if (session?.user) {
          setAuthUser(session.user);
          await loadData(session.user);
        } else {
          setNoSession(true);
          setLoading(false);
        }
      } catch (e) {
        console.error('init error', e);
        if (mounted) {
          setNoSession(true);
          setLoading(false);
        }
      }
    }

    init();
    return () => { mounted = false; };
  }, []);

  const signOut = async function() {
    await supabase.auth.signOut();
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div style={{minHeight:'100vh',background:'#F0F0EE',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'sans-serif'}}>
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:'22px',fontWeight:'800',color:'#0D0D0D',marginBottom:'16px'}}>Tam<span style={{color:'#1A7A4A'}}>bo</span></div>
          <div style={{color:'#6B6B6B',fontSize:'13px'}}>Loading your account...</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{minHeight:'100vh',background:'#F0F0EE',display:'flex',alignItems:'center',justifyContent:'center',padding:'24px',fontFamily:'sans-serif'}}>
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:'22px',fontWeight:'800',color:'#0D0D0D',marginBottom:'16px'}}>Tam<span style={{color:'#1A7A4A'}}>bo</span></div>
          <div style={{background:'#FEE2E2',color:'#DC2626',borderRadius:'10px',padding:'14px',marginBottom:'16px',fontSize:'13px'}}>{loadError}</div>
          <button onClick={function(){setLoading(true);loadData(authUser);}} style={{background:'#1A7A4A',color:'white',border:'none',borderRadius:'10px',padding:'12px 24px',fontSize:'14px',fontWeight:'700',cursor:'pointer'}}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (noSession) {
    return (
      <div style={{minHeight:'100vh',background:'#F0F0EE',display:'flex',alignItems:'center',justifyContent:'center',padding:'24px',fontFamily:'sans-serif'}}>
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:'22px',fontWeight:'800',color:'#0D0D0D',marginBottom:'16px'}}>Tam<span style={{color:'#1A7A4A'}}>bo</span></div>
          <div style={{color:'#6B6B6B',fontSize:'14px',marginBottom:'24px'}}>Please sign in to access your account.</div>
          <a href="/login" style={{background:'#1A7A4A',color:'white',padding:'12px 28px',borderRadius:'10px',fontSize:'14px',fontWeight:'700',textDecoration:'none'}}>Sign In</a>
        </div>
      </div>
    );
  }

  if (!profile || profile.client_status === 'pending' || !profile.client_status) {
    return <PendingPage onSignOut={signOut} />;
  }

  if (profile.client_status === 'rejected') {
    return (
      <div style={{minHeight:'100vh',background:'#FAFAF8',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'32px 24px',textAlign:'center',fontFamily:'sans-serif'}}>
        <div style={{fontSize:'26px',fontWeight:'800',color:'#0D0D0D',marginBottom:'32px'}}>Tam<span style={{color:'#1A7A4A'}}>bo</span></div>
        <div style={{width:'72px',height:'72px',background:'#FEE2E2',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'32px',marginBottom:'20px'}}>✗</div>
        <div style={{fontSize:'20px',fontWeight:'800',color:'#0D0D0D',marginBottom:'10px'}}>Account Not Approved</div>
        <div style={{fontSize:'14px',color:'#6B6B6B',maxWidth:'300px',lineHeight:'1.7',marginBottom:'36px'}}>
          Your account was not approved. Questions? Call <a href="tel:+19562263903" style={{color:'#1A7A4A',fontWeight:'600',textDecoration:'none'}}>(956) 226-3903</a>
        </div>
        <button onClick={signOut} style={{background:'#6B6B6B',color:'white',border:'none',borderRadius:'10px',padding:'12px 28px',fontSize:'14px',fontWeight:'700',cursor:'pointer'}}>
          Log Out
        </button>
      </div>
    );
  }

  return (
    <ClientDashboard
      user={authUser}
      profile={profile}
      orders={orders}
      invoices={invoices}
      onSignOut={signOut}
      onRefresh={function() { loadData(authUser); }}
    />
  );
}

export default Client;
